import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgProject } from '../contexts/OrgProjectContext';
import PageLayout from '../components/PageLayout';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  navy:       '#0F172A',
  navyMid:    '#1E293B',
  navyLight:  '#334155',
  blue:       '#3B82F6',
  blueLt:     '#DBEAFE',
  green:      '#10B981',
  white:      '#FFFFFF',
  slate:      '#64748B',
  slateLight: '#94A3B8',
  border:     '#E2E8F0',
  bg:         '#F8FAFC',
  text:       '#1E293B',
  textMid:    '#475569',
  red:        '#EF4444',
  redLt:      '#FEE2E2',
  yellow:     '#F59E0B',
  yellowLt:   '#FEF3C7',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ─── Validation ───────────────────────────────────────────────────────────────

function validateName(name) {
  if (!name || name.trim().length === 0) return 'Name is required';
  if (name.length > 100) return 'Name must be 100 characters or fewer';
  return null;
}

function validateDescription(desc) {
  if (desc && desc.length > 500) return 'Description must be 500 characters or fewer';
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function OrgManagement() {
  const navigate = useNavigate();
  const { orgId, setOrg } = useOrgProject();

  // List state
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const perPage = 20;

  // Form state
  const [showForm, setShowForm] = useState(false); // 'create' | 'edit' | false
  const [editingOrg, setEditingOrg] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formApiError, setFormApiError] = useState(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  // ── Fetch orgs ────────────────────────────────────────────────────────────
  const fetchOrgs = useCallback(async (pageNum = 1) => {
    setLoading(true);
    setListError(null);
    try {
      const res = await apiFetch(`/api/orgs?page=${pageNum}&per_page=${perPage}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load organisations (${res.status})`);
      }
      const data = await res.json();
      const orgList = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
      // Sort by name ascending
      orgList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setOrgs(orgList);
      setHasMore(orgList.length === perPage);
    } catch (err) {
      setListError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs(page);
  }, [fetchOrgs, page]);

  // ── Toast auto-dismiss ────────────────────────────────────────────────────
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ── Form handlers ─────────────────────────────────────────────────────────
  const openCreateForm = () => {
    setShowForm('create');
    setEditingOrg(null);
    setFormName('');
    setFormDescription('');
    setFormErrors({});
    setFormApiError(null);
  };

  const openEditForm = (org) => {
    setShowForm('edit');
    setEditingOrg(org);
    setFormName(org.name || '');
    setFormDescription(org.description || '');
    setFormErrors({});
    setFormApiError(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingOrg(null);
    setFormErrors({});
    setFormApiError(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormApiError(null);

    // Validate
    const nameErr = validateName(formName);
    const descErr = validateDescription(formDescription);
    if (nameErr || descErr) {
      setFormErrors({ name: nameErr, description: descErr });
      return;
    }
    setFormErrors({});
    setFormSubmitting(true);

    try {
      const body = { name: formName.trim(), description: formDescription.trim() || '' };
      const isEdit = showForm === 'edit' && editingOrg;
      const url = isEdit
        ? `/api/orgs/${editingOrg.id}`
        : `/api/orgs`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        // Duplicate name — show inline on the name field
        if (errBody.code === 'DUPLICATE_FIELD' || (errBody.error && errBody.error.toLowerCase().includes('already'))) {
          setFormErrors(prev => ({ ...prev, name: 'Organisation name must be unique' }));
          return;
        }
        // 4xx validation errors — show inline in the form
        if (res.status >= 400 && res.status < 500) {
          setFormApiError(errBody.error || 'Validation error');
        } else {
          // 5xx server errors — show as toast notification
          setToast({ type: 'error', message: errBody.error || `Server error (${res.status})` });
        }
        return;
      }

      closeForm();
      setToast({ type: 'success', message: isEdit ? 'Organisation updated' : 'Organisation created' });
      fetchOrgs(page);
    } catch (err) {
      // Network errors — show as toast notification
      setToast({ type: 'error', message: err.message || 'Network error' });
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Delete handlers ───────────────────────────────────────────────────────
  const confirmDelete = (org) => {
    setDeleteTarget(org);
    setDeleteError(null);
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);

    try {
      const res = await apiFetch(`/api/orgs/${deleteTarget.id}`, { method: 'DELETE' });

      if (res.status === 409) {
        const errBody = await res.json().catch(() => ({}));
        setDeleteError(errBody.error || 'Cannot delete: organisation has projects');
        return;
      }

      if (!res.ok && res.status !== 204) {
        const errBody = await res.json().catch(() => ({}));
        // 5xx server errors — show as toast notification
        if (res.status >= 500) {
          setToast({ type: 'error', message: errBody.error || `Failed to delete (${res.status})` });
        } else {
          setDeleteError(errBody.error || `Failed to delete (${res.status})`);
        }
        return;
      }

      setDeleteTarget(null);
      setToast({ type: 'success', message: 'Organisation deleted' });
      fetchOrgs(page);
    } catch (err) {
      // Network errors — show as toast notification
      setToast({ type: 'error', message: err.message || 'Network error' });
    } finally {
      setDeleting(false);
    }
  };

  // ── Inline validation on change ───────────────────────────────────────────
  const handleNameChange = (val) => {
    setFormName(val);
    const err = validateName(val);
    setFormErrors(prev => ({ ...prev, name: err }));
    if (formApiError) setFormApiError(null);
  };

  const handleDescriptionChange = (val) => {
    setFormDescription(val);
    const err = validateDescription(val);
    setFormErrors(prev => ({ ...prev, description: err }));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageLayout>
    <div style={styles.container}>
      {/* Toast */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'success' ? C.green : C.red,
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Organisations</h1>
          <p style={styles.subtitle}>Manage your organisations</p>
        </div>
        <button onClick={openCreateForm} style={styles.createBtn}>
          + New Organisation
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>
            {showForm === 'edit' ? 'Edit Organisation' : 'Create Organisation'}
          </h2>
          <form onSubmit={handleFormSubmit}>
            {/* Name field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="org-name">
                Name <span style={styles.required}>*</span>
              </label>
              <input
                id="org-name"
                type="text"
                value={formName}
                onChange={(e) => handleNameChange(e.target.value)}
                maxLength={100}
                placeholder="Organisation name"
                style={{
                  ...styles.input,
                  borderColor: formErrors.name ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.name}
                aria-describedby={formErrors.name ? 'org-name-error' : undefined}
              />
              {formErrors.name && (
                <span id="org-name-error" style={styles.fieldError}>{formErrors.name}</span>
              )}
              <span style={styles.charCount}>{formName.length}/100</span>
            </div>

            {/* Description field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label} htmlFor="org-desc">Description</label>
              <textarea
                id="org-desc"
                value={formDescription}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                maxLength={500}
                placeholder="Optional description"
                rows={3}
                style={{
                  ...styles.textarea,
                  borderColor: formErrors.description ? C.red : C.border,
                }}
                aria-invalid={!!formErrors.description}
                aria-describedby={formErrors.description ? 'org-desc-error' : undefined}
              />
              {formErrors.description && (
                <span id="org-desc-error" style={styles.fieldError}>{formErrors.description}</span>
              )}
              <span style={styles.charCount}>{formDescription.length}/500</span>
            </div>

            {/* API error */}
            {formApiError && (
              <div style={styles.apiError} role="alert">
                {formApiError}
              </div>
            )}

            {/* Actions */}
            <div style={styles.formActions}>
              <button type="button" onClick={closeForm} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={formSubmitting}
                style={{
                  ...styles.submitBtn,
                  opacity: formSubmitting ? 0.6 : 1,
                  cursor: formSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                {formSubmitting
                  ? 'Saving…'
                  : showForm === 'edit' ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List error */}
      {listError && (
        <div style={styles.listError} role="alert">
          {listError}
          <button onClick={() => fetchOrgs(page)} style={styles.retryBtn}>Retry</button>
        </div>
      )}

      {/* Org table */}
      {loading ? (
        <div style={styles.loadingState}>Loading organisations…</div>
      ) : orgs.length === 0 && !listError ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No organisations yet.</p>
          <button onClick={openCreateForm} style={styles.emptyBtn}>Create your first organisation</button>
        </div>
      ) : (
        <>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Projects</th>
                  <th style={styles.th}>Last Activity</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(org => (
                  <tr key={org.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={styles.orgNameCell}>{org.name}</span>
                      {org.description && (
                        <span style={styles.orgDescCell}>{org.description}</span>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <span style={styles.projectCountBadge}>
                        {org.project_count ?? 0}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.timestampCell}>
                        {formatTimestamp(org.updated_at || org.created_at)}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <button
                        onClick={() => navigate(`/orgs/${org.id}/projects`)}
                        style={styles.actionBtn}
                        aria-label={`Manage projects for ${org.name}`}
                      >
                        Projects
                      </button>
                      <button
                        onClick={() => navigate(`/orgs/${org.id}/scm-connections`)}
                        style={styles.actionBtn}
                        aria-label={`Manage SCM connections for ${org.name}`}
                      >
                        SCM
                      </button>
                      <button
                        onClick={() => openEditForm(org)}
                        style={styles.actionBtn}
                        aria-label={`Edit ${org.name}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => confirmDelete(org)}
                        style={styles.deleteBtn}
                        aria-label={`Delete ${org.name}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={styles.pagination}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                ...styles.pageBtn,
                opacity: page === 1 ? 0.4 : 1,
                cursor: page === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              ← Previous
            </button>
            <span style={styles.pageInfo}>Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
              style={{
                ...styles.pageBtn,
                opacity: !hasMore ? 0.4 : 1,
                cursor: !hasMore ? 'not-allowed' : 'pointer',
              }}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="delete-dialog-title">
          <div style={styles.dialog}>
            <h3 id="delete-dialog-title" style={styles.dialogTitle}>Delete Organisation</h3>
            <p style={styles.dialogText}>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
              This action cannot be undone.
            </p>
            {deleteError && (
              <div style={styles.dialogError} role="alert">{deleteError}</div>
            )}
            <div style={styles.dialogActions}>
              <button onClick={cancelDelete} style={styles.cancelBtn}>Cancel</button>
              <button
                onClick={executeDelete}
                disabled={deleting}
                style={{
                  ...styles.confirmDeleteBtn,
                  opacity: deleting ? 0.6 : 1,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                }}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PageLayout>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  container: {
    padding: '24px 32px',
    maxWidth: 960,
    margin: '0 auto',
    position: 'relative',
  },
  toast: {
    position: 'fixed',
    top: 20,
    right: 20,
    padding: '10px 20px',
    borderRadius: 8,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    zIndex: 10001,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: C.text,
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: C.slate,
    margin: '4px 0 0',
  },
  createBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(59,130,246,0.3)',
  },
  // Form
  formCard: {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: '20px 24px',
    marginBottom: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  formTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 16px',
  },
  fieldGroup: {
    marginBottom: 16,
    position: 'relative',
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: C.textMid,
    marginBottom: 4,
  },
  required: {
    color: C.red,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    fontSize: 13,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    outline: 'none',
    color: C.text,
    background: C.white,
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '9px 12px',
    fontSize: 13,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    outline: 'none',
    color: C.text,
    background: C.white,
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  fieldError: {
    display: 'block',
    fontSize: 11,
    color: C.red,
    marginTop: 4,
  },
  charCount: {
    position: 'absolute',
    right: 0,
    top: 0,
    fontSize: 10,
    color: C.slateLight,
  },
  apiError: {
    padding: '10px 14px',
    borderRadius: 6,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 16,
    border: `1px solid ${C.red}20`,
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelBtn: {
    padding: '8px 16px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '8px 18px',
    borderRadius: 6,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  // List
  listError: {
    padding: '12px 16px',
    borderRadius: 8,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  retryBtn: {
    padding: '4px 12px',
    borderRadius: 4,
    border: `1px solid ${C.red}`,
    background: 'transparent',
    color: C.red,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px 0',
    color: C.slate,
    fontSize: 13,
    fontStyle: 'italic',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 0',
  },
  emptyText: {
    color: C.slate,
    fontSize: 14,
    margin: '0 0 12px',
  },
  emptyBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    border: 'none',
    background: C.blue,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.white,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: C.slate,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: `1px solid ${C.border}`,
    background: C.bg,
  },
  tr: {
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: '12px 16px',
    verticalAlign: 'middle',
  },
  orgNameCell: {
    display: 'block',
    fontWeight: 600,
    color: C.text,
    fontSize: 13,
  },
  orgDescCell: {
    display: 'block',
    fontSize: 11,
    color: C.slate,
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 300,
  },
  projectCountBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 99,
    background: C.blueLt,
    color: C.blue,
    fontSize: 12,
    fontWeight: 700,
  },
  timestampCell: {
    fontSize: 12,
    color: C.slate,
  },
  actionBtn: {
    padding: '5px 12px',
    borderRadius: 5,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: 6,
  },
  deleteBtn: {
    padding: '5px 12px',
    borderRadius: 5,
    border: `1px solid ${C.red}40`,
    background: 'transparent',
    color: C.red,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
  },
  pageBtn: {
    padding: '6px 14px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.textMid,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  pageInfo: {
    fontSize: 12,
    color: C.slate,
  },
  // Delete dialog
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10002,
  },
  dialog: {
    background: C.white,
    borderRadius: 12,
    padding: '24px 28px',
    maxWidth: 420,
    width: '90%',
    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
  },
  dialogTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 12px',
  },
  dialogText: {
    fontSize: 13,
    color: C.textMid,
    margin: '0 0 16px',
    lineHeight: 1.5,
  },
  dialogError: {
    padding: '10px 14px',
    borderRadius: 6,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 16,
    border: `1px solid ${C.red}20`,
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  confirmDeleteBtn: {
    padding: '8px 18px',
    borderRadius: 6,
    border: 'none',
    background: C.red,
    color: C.white,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
};
